<template>
  <div class="executive-view-kpi" sticky-container>
    <div class="executive-view-kpi-scores-tag-wrapper">
      <KpiScores v-model="selectedKpi"
                 :project-id="projectId"
                 :collected-tags="collectedTags"
                 :periods="periods"
                 :auditor-and-store-filters="auditorAndStoreFilters"
                 :team-group-filter="teamGroupFilter" />

      <div v-sticky="shouldStick" class="sticky-bar">
        <div v-if="showCollectedTags" class="category-tag-list">
          <span v-for="item in collectedTags" :key="item.root.id">
            <div v-for="tag in item.tags"
                 :key="tag.id"
                 class="tagButton"
                 size="mini">
              <span> {{ tag.name }} </span>
              <i class="mdi mdi-close-circle delete-tag" @click="deleteTag(tag)" />
            </div>
          </span>
        </div>
      </div>
    </div>

    <div v-if="selectedKpi" class="executive-view-kpi__content">
      <el-row :gutter="20" class="executive-view-kpi__row">
        <el-col class="category-trends-col"
                :xs="24"
                :sm="24"
                :lg="8">
          <CategoryTrends :periods="periods"
                          :project-id="projectId"
                          :collected-tags="collectedTags"
                          :selected-kpi="selectedKpi"
                          :auditor-and-store-filters="auditorAndStoreFilters"
                          :team-group-filter="teamGroupFilter" />
        </el-col>
        <el-col class="kpi-trends-col"
                :xs="24"
                :sm="24"
                :lg="16">
          <KpiTrends :periods="periods"
                     :project-id="projectId"
                     :collected-tags="collectedTags"
                     :selected-kpi="selectedKpi"
                     :auditor-and-store-filters="auditorAndStoreFilters"
                     :team-group-filter="teamGroupFilter" />
        </el-col>
      </el-row>

      <SortSelect
        v-if="storeFilters.length"
        v-model="sortValue"
        :options="sortOptions"
        style="margin-bottom: 20px;" />

      <el-row :gutter="20">
        <el-col v-for="(filter,index) in storeFilters"
                :key="index"
                :span="expandedGraphs.indexOf(filter.id) !== -1 ? 24 : 12"
                :xs="24"
                class="kpi-score-by-tags-col">
          <KpiScoreByTags v-for="tagType in filter.tag_type"
                          ref="kpiScoreByTags"
                          :key="tagType.id"
                          :collected-tags="collectedTags"
                          :project-id="projectId"
                          :periods="periods"
                          :tag-type="tagType"
                          :selected-kpi="selectedKpi"
                          :filter-title="filter.title"
                          :auditor-and-store-filters="auditorAndStoreFilters"
                          :team-group-filter="teamGroupFilter"
                          :is-btn-expand="expandedGraphs.indexOf(filter.id) !== -1"
                          :tag-index="index"
                          :filter-id="filter.id"
                          :sort-value="sortValue"
                          @expandChart="handleExpandToggle(...arguments, $refs.kpiScoreByTags)" />
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { chain } from 'lodash';
import Sticky from 'vue-sticky-directive';
import KpiScores from './components/KpiScores';
import CategoryTrends from './components/CategoryTrends';
import KpiScoreByTags from './components/KpiScoreByTags';
import KpiTrends from './components/KpiTrends';
import SortSelect from '../executive-view/components/SortSelect';
import ExecutiveViewKpiBus from './bus';
import expandChartToggle from '../../helpers/mixins/expandChartToggle';

export default {
  name: 'ExecutiveViewKpi',
  directives: { Sticky },
  components: {
    KpiScores,
    CategoryTrends,
    KpiScoreByTags,
    KpiTrends,
    SortSelect
  },
  mixins: [expandChartToggle],
  props: {
    periods: {
      type: Object,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    auditorAndStoreFilters: {
      type: Object,
      required: true
    },
    teamGroupFilter: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ExecutiveViewKpiBus,
      shouldStick: true,
      selectedKpi: '',
      collectedTags: [],
      sortValue: 'name'
    };
  },
  computed: {
    ...mapGetters(['globalStoreFilters']),
    showCollectedTags() {
      return this.collectedTags.find(item => item.tags.length > 0);
    },
    storeFilters() {
      return chain(this.globalStoreFilters)
        .filter(item => item.drildown_in_executive_view)
        .value();
    },
    sortOptions() {
      const nameOptionGroup = {
        label: this.$t('by Name'),
        options: [
          {
            label: 'A-Z',
            value: 'name'
          }
        ]
      };
      const categoryOptionGroup = {
        label: this.$t('by Category'),
        options: []
      };

      this.ExecutiveViewKpiBus.legends.forEach((category) => {
        if (category.visible) categoryOptionGroup.options.push({ label: category.name, value: category.name });
      });

      return { nameOptionGroup, categoryOptionGroup };
    }
  },
  beforeDestroy() {
    ExecutiveViewKpiBus.legends = [];
    ExecutiveViewKpiBus.$off();
  },
  activated() {
    this.shouldStick = true;
  },
  deactivated() {
    this.shouldStick = false;
  },
  mounted() {
    ExecutiveViewKpiBus.$on('TAG_FILTERED', () => {
      this.collectedTags = this.$refs.kpiScoreByTags.reduce((acc, component) => {
        acc.push({
          root: component.tagType,
          tags: component.visitedTags
        });

        return acc;
      }, []);
    });
  },
  methods: {
    deleteTag(tag) {
      ExecutiveViewKpiBus.$emit('DELETE_TAG', tag);
    }
  }
};
</script>

<style lang="scss">
.executive-view-kpi {
  position: relative;
  width: 100%;

  &-scores-tag-wrapper {
    display: block;
    background-color: white;

    @include breakpoint(lg-max) {
      background-color: transparent;
    }

    @include breakpoint(md-s-max) {
      padding-top: 8px;
      border-top: 1px solid #ddd;
    }
  }

  &__row {
    margin-right: unset !important;
    margin-bottom: 20px;
    margin-left: unset !important;
    background-color: $white;

    @include breakpoint(lg-max) {
      border-radius: 6px;
    }
  }

  &__content {
    padding: 20px;

    .title {
      margin-bottom: 20px;
      font-size: 22px;
    }

    .score-value {
      font-size: 25px;
      text-align: right;

      @include breakpoint(lg-max) {
        font-size: 21px;
      }
    }

    .score-name {
      flex: 1;
      margin-left: 10px;
      overflow: hidden;
      font-size: 14px;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .averages-list {
      max-width: 700px;

      @include breakpoint(lg-max) {
        max-width: 100%;
      }

      .item {
        display: flex;
        align-items: center;
        padding: 4px 0;

        &:not(:last-child) {
          border-bottom: 1px solid $greyArea;
        }
      }
    }
  }
}

@include breakpoint(lg-max) {
  .executive-view-kpi {
    &__content {
      padding: 10px 5px 20px 5px;

      .kpi-view-trends,
      .kpi-score-by-tags {
        padding: 10px;
      }

      .el-row {
        margin-right: 5px !important;
        margin-left: 5px !important;

        .el-col {
          padding-right: 0 !important;
          padding-left: 0 !important;
        }
      }
    }

    .category-trends-col,
    .kpi-trends-col,
    .kpi-score-by-tags-col {
      padding: 0;
      border-radius: 6px;
    }
  }
}
</style>
